import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { hideUpgradeModal, GOAT_FEATURES_MAP } from '../../redux/slices/tierSlice';
import mixpediaIcon from '../../assets/icons/categories/mixpedia.svg';
import whatsNextIcon from '../../assets/icons/categories/whatsnext.svg';
import recommendationsIcon from '../../assets/icons/categories/recommendations.svg';
import loudnessIcon from '../../assets/icons/categories/loudness.svg';
import metadataIcon from '../../assets/icons/categories/metadata.svg';
import organizeIcon from '../../assets/icons/categories/organize.svg';
import './GoatUpgradeModal.scss';
import { MIXPEDIA_FREE_FOR_ALL } from '../../../backend/planModel';
import { useDesignV2 } from '../../utils/designV2';
import GoatMark from '../GoatMark';

const CLOUD_BACKUP_BENEFIT = {
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>,
    label: 'Cloud Backup — cues, loops, playlists & files from every DJ app',
};

// The lead benefits follow the plan model (src/backend/planModel.js): while
// Mixpedia / For You are GOAT they headline; once they are free on every
// plan, Cloud Backup and Set Planner take their place. Cloud Backup is listed
// either way — the Library's backup-off line and the Backup page open this
// wall saying "Cloud Backup is part of GOAT", so the list has to show it.
const LEAD_BENEFITS = MIXPEDIA_FREE_FOR_ALL
    ? [
        CLOUD_BACKUP_BENEFIT,
        { iconSrc: whatsNextIcon, label: 'Set Planner — plan full-length sets with real DJ data' },
    ]
    : [
        { iconSrc: mixpediaIcon, label: "Full Mixpedia — Find any DJ's IDs and who plays yours" },
        { iconSrc: recommendationsIcon, label: 'For You — personalized feed based on your likes and artists' },
        CLOUD_BACKUP_BENEFIT,
    ];

const GOAT_BENEFITS = [
    ...LEAD_BENEFITS,
    { iconSrc: recommendationsIcon, label: 'Smart Recommendations — discover tracks from coplay data' },
    { iconSrc: loudnessIcon, label: 'Loudness Matching — normalize LUFS across your library' },
    { iconSrc: metadataIcon, label: 'Metadata Repair — fetch & write pro-grade tags' },
    { iconSrc: organizeIcon, label: 'Smart Organize — restructure folders with metadata' },
    { icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>, label: 'Notification bells — know the moment an artist drops a set or your liked track gets played' },
    { icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>, label: 'Priority Support' },
];

// Fallback if the fetch fails entirely (offline / backend down) — never
// rendered when the fetch resolves normally, just keeps the modal usable.
const FALLBACK_PRICING = { currency: 'usd', symbol: '$', monthly: '9.99', annual: '89', savePercent: 26 };

const GoatUpgradeModal = () => {
    const dispatch = useDispatch();
    const show = useSelector((state) => state.tier.showUpgradeModal);
    const featureKey = useSelector((state) => state.tier.upgradeFeature);
    const trialExpired = useSelector((state) => state.tier.isTrialExpired);
    const user = useSelector((state) => state.auth.user);
    const v2 = useDesignV2();

    const featureLabel = featureKey ? GOAT_FEATURES_MAP[featureKey] : '';

    // null until first fetch lands — we render a skeleton in the price slot
    // rather than a placeholder $ that flashes to € for EU customers. The
    // modal is open ~10s typically so even on a slow fetch the skeleton is
    // visible briefly then swaps to real values once.
    const [pricing, setPricing] = useState(null);

    useEffect(() => {
        if (show) {
            window.electron.analytics.track('upgrade_modal_shown', {
                feature: featureKey,
                trial_expired: trialExpired,
            });
            (async () => {
                try {
                    const result = await window.electron.subscription.getPricing();
                    if (result && !result.error && result.monthly) {
                        setPricing(result);
                    } else {
                        // Endpoint returned an error — render the static fallback
                        // so the modal isn't permanently stuck in skeleton state.
                        setPricing(FALLBACK_PRICING);
                    }
                } catch {
                    setPricing(FALLBACK_PRICING);
                }
            })();
        }
    }, [show, featureKey, trialExpired]);

    const handleClose = () => {
        window.electron.analytics.track('upgrade_modal_dismissed', { feature: featureKey });
        dispatch(hideUpgradeModal());
    };

    const handleSubscribe = () => {
        window.electron.analytics.track('subscribe_clicked', { feature: featureKey });
        const params = new URLSearchParams();
        if (user?.id) params.set('userId', user.id);
        if (user?.email) params.set('email', user.email);
        // Carries the gate that sold the upgrade into the website's checkout
        // events (feature → conversion funnel in the ops Intelligence tab).
        if (featureKey) params.set('feature', featureKey);
        const checkoutUrl = `https://mixgoat.com/checkout${params.toString() ? '?' + params.toString() : ''}`;
        window.electron.openUrl(checkoutUrl);
        handleClose();
    };

    return (
        <Modal show={show} onHide={handleClose} centered className="goat-upgrade-modal" size="md">
            <Modal.Body className="goat-upgrade-body">
                <div className="goat-upgrade-icon">
                    {v2 ? <GoatMark size={48} /> : (
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                        </svg>
                    )}
                </div>

                {featureLabel && (
                    <h4 className="goat-upgrade-feature-label">
                        {featureLabel} is a GOAT feature
                    </h4>
                )}

                <h3 className="goat-upgrade-title">
                    {trialExpired
                        ? 'Your trial has ended'
                        : 'Upgrade to GOAT'}
                </h3>

                <p className="goat-upgrade-subtitle">
                    {trialExpired
                        ? 'Subscribe to keep using all premium features.'
                        : 'Unlock the full power of MixGoat.'}
                </p>

                <ul className="goat-benefits-list">
                    {GOAT_BENEFITS.map((b, i) => (
                        <li key={i} className="goat-benefit-item">
                            {b.iconSrc
                                ? <img src={b.iconSrc} alt="" className="goat-benefit-icon" />
                                : <span className="goat-benefit-icon">{b.icon}</span>
                            }
                            <span className="goat-benefit-label">{b.label}</span>
                        </li>
                    ))}
                </ul>

                <div className="goat-pricing">
                    {pricing ? (
                        <>
                            <div className="goat-price-option">
                                <span className="goat-price-amount">{pricing.symbol}{pricing.monthly}</span>
                                <span className="goat-price-period">/month</span>
                            </div>
                            <span className="goat-price-divider">or</span>
                            <div className="goat-price-option goat-price-annual">
                                <span className="goat-price-amount">{pricing.symbol}{pricing.annual}</span>
                                <span className="goat-price-period">/year</span>
                                <span className="goat-price-save">Save {pricing.savePercent}%</span>
                            </div>
                        </>
                    ) : (
                        // Skeleton — preserves the modal's height + the price
                        // block's layout so nothing shifts once pricing arrives.
                        <>
                            <div className="goat-price-option">
                                <span className="goat-price-skeleton goat-price-skeleton-amount" aria-hidden="true" />
                                <span className="goat-price-skeleton goat-price-skeleton-period" aria-hidden="true" />
                            </div>
                            <span className="goat-price-divider">or</span>
                            <div className="goat-price-option goat-price-annual">
                                <span className="goat-price-skeleton goat-price-skeleton-amount" aria-hidden="true" />
                                <span className="goat-price-skeleton goat-price-skeleton-period" aria-hidden="true" />
                            </div>
                        </>
                    )}
                </div>

                <div className="goat-upgrade-actions">
                    <Button
                        variant="warning"
                        className="goat-subscribe-btn"
                        onClick={handleSubscribe}
                    >
                        Subscribe to GOAT
                    </Button>
                </div>

                <button className="goat-close-btn" onClick={handleClose}>
                    Maybe later
                </button>
            </Modal.Body>
        </Modal>
    );
};

export default GoatUpgradeModal;
